/**
 * File: assets/basari-cart-guard.js
 * Purpose: Every way to pay from the cart goes through here.
 *
 *   DRAFT ORDER CHECKOUT (a cart line carries _basari_rx, added by
 *   basari-api-integration.js when the backend's checkout mode is DRAFT_ORDER):
 *   - The cart drawer and cart page are a REVIEW cart. Their lines sit on the
 *     product's own Shopify variant, whose Shopify price is not what is
 *     charged, so the backend's price for each prescription line is shown
 *     instead (POST /api/storefront/checkout/preview), with any line it can no
 *     longer sell marked, and a note that shipping and tax follow on the
 *     payment page.
 *   - Checkout asks POST /api/storefront/checkout for a Shopify Draft Order:
 *     the backend prices every line again, holds the stock and returns the
 *     Draft Order's invoice URL, and only then does the browser go there. The
 *     button is disabled while it works; the same cart always reuses the same
 *     idempotency key, so a second click, a refresh or a network retry never
 *     creates a second Draft Order. Anything that fails leaves the cart as it
 *     was, with the reason shown.
 *
 *   OLDER VARIANT CHECKOUT (no such line): as before, the cart is re-checked
 *   (POST /api/storefront/cart/validate) and then Shopify's /checkout opens.
 *
 * LIMIT: this runs in the shopper's browser. /checkout typed in directly skips
 * it; the backend's order webhook flags such orders for staff.
 *
 * SETUP: upload to assets/, then in layout/theme.liquid, before </body>:
 *   <script src="{{ 'basari-cart-guard.js' | asset_url }}" defer="defer"></script>
 * Backend address: Theme settings > Basari backend (shared with basari-api-integration.js).
 */
(function () {
  'use strict';

  // Backend address: Theme settings > Basari backend (window.BasariConfig,
  // set in layout/theme.liquid). The fallback is for local test pages only.
  // Empty = no checks: an ordinary cart goes to Shopify's checkout as if this
  // file were not installed; a Draft Order cart is told payment is unavailable.
  var API_BASE_URL_FALLBACK = '';
  var API_BASE_URL = (function (fallback) {
    var configured = window.BasariConfig && window.BasariConfig.apiBaseUrl;
    var raw = String(configured || fallback || '').trim().replace(/\/+$/, '');
    return /^https:\/\/[a-z0-9.-]+(:\d+)?(\/[^\s]*)?$/i.test(raw) ? raw : '';
  })(API_BASE_URL_FALLBACK);
  var PAYMENT_OFF = 'Online payment is not available right now. Please contact us to order.';
  var CHECKOUT_SELECTOR = '[name="checkout"], [data-basari-draft-pay], a[href="/checkout"], a[href$="/checkout"]';
  var passed = false; // set once a check succeeds, so the re-fired click goes through

  var MANAGED_KEY = 'basari:managed:'; // set by basari-api-integration.js
  var KEY_STORE = 'basari:checkout-key';
  var UNCHECKED = 'We could not confirm the prices of your prescription lenses just now. Please try again in a moment.';
  var NETWORK = 'We could not reach the payment system. Your cart has not been changed. Please try again.';
  var MAX_WAITS = 4;

  function seenAsManaged(productId) {
    try {
      return window.localStorage.getItem(MANAGED_KEY + productId) === '1';
    } catch (e) {
      return false;
    }
  }

  function api(path, body) {
    return fetch(API_BASE_URL + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'ngrok-skip-browser-warning': 'true' },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        return res.json().then(
          function (json) { return { status: res.status, body: json }; },
          function () { return { status: res.status, body: null }; }
        );
      })
      .catch(function () {
        return { status: 0, body: null };
      });
  }

  function readCart() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(function (res) {
      return res.json();
    });
  }

  function isDraftLine(item) {
    return !!(item && item.properties && item.properties._basari_rx);
  }
  function hasDraftLines(cart) {
    return (cart.items || []).some(isDraftLine);
  }

  // ---- older variant checkout --------------------------------------------
  // Every line, with its product and (when it has one) its _basari_sku: the
  // backend refuses a line of a product priced there that does not carry one
  // of that product's own SKUs (a collection page's quick add, the theme's own
  // form, an edited request), as well as a SKU line that is off, out of stock,
  // or no longer on its own variant at its own price.
  function cartLines(cart) {
    return (cart.items || []).map(function (item) {
      var sku = item.properties && item.properties._basari_sku;
      return {
        key: String(item.key),
        sku: sku ? String(sku) : null,
        productId: String(item.product_id),
        quantity: item.quantity,
        title: item.product_title,
        // Lets the backend confirm the line still sits on the SKU's own variant
        // at the SKU's own price.
        variantId: String(item.variant_id),
        unitPrice: item.original_price / 100
      };
    });
  }

  // A cart that may hold a line priced by the backend cannot go to checkout
  // unchecked: if the backend cannot answer, the shopper is asked to try again.
  // Any other cart is never held up.
  function mustCheck(lines) {
    return lines.some(function (line) {
      return line.sku || seenAsManaged(line.productId);
    });
  }

  function validate(cart) {
    var lines = cartLines(cart);
    if (!lines.length) return Promise.resolve({ valid: true, lines: [] });
    return api('/api/storefront/cart/validate', { lines: lines }).then(function (r) {
      if (r.body && r.body.data) return r.body.data;
      if (mustCheck(lines)) return { valid: false, lines: [{ ok: false, message: UNCHECKED }] };
      return { valid: true, lines: [] };
    });
  }

  function refusalText(result) {
    var messages = (result.lines || [])
      .filter(function (line) { return !line.ok && line.message; })
      .map(function (line) { return line.message; });
    return 'Please update your cart before checking out:\n\n' + Array.from(new Set(messages)).join('\n');
  }

  // ---- Draft Order checkout ------------------------------------------------
  var lastPreview = null; // { signature, data }

  // The cart's lines as the backend reads them: no price or total is sent
  // except the unit price the review cart showed, which is only compared.
  function draftLines(cart) {
    var shown = {};
    if (lastPreview && lastPreview.data) {
      (lastPreview.data.lines || []).forEach(function (l) {
        if (l.unitPrice != null) shown[l.key] = l.unitPrice;
      });
    }
    return (cart.items || []).map(function (item) {
      var props = {};
      Object.keys(item.properties || {}).forEach(function (k) {
        var v = item.properties[k];
        if (v == null || typeof v === 'object') return;
        props[k] = v;
      });
      var expected = shown[item.key];
      if (expected == null && props._basari_price != null && isFinite(Number(props._basari_price))) expected = Number(props._basari_price);
      var plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
      return {
        key: String(item.key),
        productId: String(item.product_id),
        variantId: String(item.variant_id),
        quantity: item.quantity,
        title: item.product_title,
        sellingPlanId: plan ? String(plan.id) : null,
        expectedUnitPrice: isDraftLine(item) && expected != null ? Number(expected) : null,
        properties: props
      };
    });
  }

  // The same cart (lines, quantities, prescriptions) always maps to the same
  // key, so a repeat click, a reload or a retry is recognised by the backend.
  function signature(cart) {
    var parts = (cart.items || []).map(function (i) {
      var p = i.properties || {};
      return [i.key, i.quantity, p._basari_rx || '', p._basari_sku || ''].join('|');
    });
    parts.sort();
    return (cart.token || '') + '#' + parts.join(',');
  }
  function randomKey() {
    var bytes = new Uint8Array(16);
    if (window.crypto && window.crypto.getRandomValues) window.crypto.getRandomValues(bytes);
    else for (var i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
    return 'bco_' + Array.prototype.map.call(bytes, function (b) { return ('0' + b.toString(16)).slice(-2); }).join('');
  }
  function keyFor(sig, fresh) {
    var stored = null;
    try {
      stored = JSON.parse(window.sessionStorage.getItem(KEY_STORE) || 'null');
    } catch (e) {
      stored = null;
    }
    if (!fresh && stored && stored.sig === sig && stored.key) return stored.key;
    var key = randomKey();
    try {
      window.sessionStorage.setItem(KEY_STORE, JSON.stringify({ sig: sig, key: key }));
    } catch (e) {
      /* the key still works for this page */
    }
    return key;
  }

  function checkoutButtons() {
    return Array.prototype.slice.call(document.querySelectorAll(CHECKOUT_SELECTOR));
  }
  function setBusy(busy) {
    checkoutButtons().forEach(function (b) {
      if (busy) {
        b.setAttribute('aria-busy', 'true');
        if ('disabled' in b) b.disabled = true;
        b.setAttribute('data-basari-busy', '');
      } else if (b.hasAttribute('data-basari-busy')) {
        b.removeAttribute('aria-busy');
        if ('disabled' in b) b.disabled = false;
        b.removeAttribute('data-basari-busy');
      }
    });
  }

  // A message next to every visible checkout button (drawer and page). With
  // the cart now empty there are no buttons: it goes in the empty-cart notice.
  function showCheckoutMessage(text) {
    var buttons = checkoutButtons();
    if (!buttons.length) {
      Array.prototype.forEach.call(document.querySelectorAll('[data-pdc-empty]'), function (box) {
        var note = box.querySelector('[data-basari-checkout-message]');
        if (!note && text) {
          note = document.createElement('p');
          note.setAttribute('data-basari-checkout-message', '');
          note.setAttribute('role', 'alert');
          note.style.cssText = 'margin:0 0 12px;font-size:14px;line-height:1.4;';
          box.insertBefore(note, box.firstChild);
        }
        if (note) {
          note.textContent = text || '';
          note.hidden = !text;
        }
      });
      return;
    }
    buttons.forEach(function (b) {
      var host = b.closest('form') || b;
      var note = host.parentNode ? host.parentNode.querySelector('[data-basari-checkout-message]') : null;
      if (!note && text && host.parentNode) {
        note = document.createElement('p');
        note.setAttribute('data-basari-checkout-message', '');
        note.setAttribute('role', 'alert');
        note.style.cssText = 'margin:8px 0 0;font-size:13px;line-height:1.4;color:#b42318;white-space:pre-line;';
        host.parentNode.insertBefore(note, host.nextSibling);
      }
      if (note) {
        note.textContent = text || '';
        note.hidden = !text;
      }
    });
  }

  function wait(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  function safeInvoiceUrl(url) {
    try {
      var u = new URL(url);
      return u.protocol === 'https:' ? u.href : null;
    } catch (e) {
      return null;
    }
  }

  var inFlight = null;

  // Resolves 'redirected', 'fallback' (the backend does not use Draft Orders:
  // run the older checkout) or 'stopped' (a message is shown, cart unchanged).
  function startDraftCheckout(cart) {
    if (inFlight) return inFlight;
    var sig = signature(cart);
    var key = keyFor(sig, false);
    var renewed = false;
    var waits = 0;
    setBusy(true);
    showCheckoutMessage('');

    function attempt() {
      return api('/api/storefront/checkout', { idempotencyKey: key, cartToken: cart.token || null, lines: draftLines(cart) }).then(function (r) {
        var body = r.body || {};
        var data = body.data || {};
        var err = body.error || {};
        if (r.status === 200 || r.status === 201) {
          var url = safeInvoiceUrl(data.invoiceUrl);
          if (!url) {
            showCheckoutMessage('The payment page could not be opened. Your cart has not been changed. Please try again.');
            return 'stopped';
          }
          window.location.assign(url);
          return 'redirected';
        }
        if (err.code === 'DRAFT_CHECKOUT_OFF' || err.code === 'NOT_NEEDED') return 'fallback';
        // Paying the invoice does not empty the store's cart. This exact cart
        // was already ordered: empty it, so it is not paid for twice.
        if (err.code === 'ALREADY_ORDERED') {
          return fetch('/cart/clear.js', { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' } })
            .catch(function () { return null; })
            .then(function () {
              var premium = window.PremiumCart;
              return premium && typeof premium.refresh === 'function' ? Promise.resolve(premium.refresh()).catch(function () {}) : null;
            })
            .then(function () {
              showCheckoutMessage((err.message || 'This cart has already been ordered.') + ' Your cart has been emptied.');
              return 'stopped';
            });
        }
        // This attempt ended (expired, closed): start a fresh one, once.
        if (r.status === 410 && !renewed) {
          renewed = true;
          key = keyFor(sig, true);
          return attempt();
        }
        // Being prepared, or Shopify briefly unreachable: wait and ask again
        // with the SAME key, which never creates a second Draft Order.
        if ((err.code === 'IN_PROGRESS' || err.code === 'RETRY') && waits < MAX_WAITS) {
          waits += 1;
          var after = Math.min(Number(err.retryAfterSeconds) || 3, 10);
          return wait(after * 1000).then(attempt);
        }
        if (r.status === 0) {
          showCheckoutMessage(NETWORK);
          return 'stopped';
        }
        showCheckoutMessage(err.message || 'The payment page could not be prepared. Your cart has not been changed.');
        // Prices or stock may have moved: show the cart as it now stands.
        if (err.code === 'PRICE_CHANGED' || err.code === 'OUT_OF_STOCK' || err.code === 'CART_INVALID') refreshReview(true);
        return 'stopped';
      });
    }

    inFlight = attempt()
      .catch(function () {
        showCheckoutMessage(NETWORK);
        return 'stopped';
      })
      .then(function (outcome) {
        // A redirect keeps the buttons locked until the page leaves.
        if (outcome !== 'redirected') setBusy(false);
        inFlight = null;
        return outcome;
      });
    return inFlight;
  }

  // ---- review cart ---------------------------------------------------------
  function moneyFormat() {
    var el = document.querySelector('[data-money-format]');
    return (el && el.getAttribute('data-money-format')) || '{{amount}}';
  }
  function formatMoney(amount) {
    var cents = Math.round(Number(amount) * 100);
    var fmt = moneyFormat();
    function group(n, decimals, thousands, decimal) {
      var fixed = (n / 100).toFixed(decimals).split('.');
      var whole = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return whole + (fixed[1] ? decimal + fixed[1] : '');
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, name) {
      if (name === 'amount_no_decimals') return group(cents, 0, ',', '.');
      if (name === 'amount_with_comma_separator') return group(cents, 2, '.', ',');
      if (name === 'amount_no_decimals_with_comma_separator') return group(cents, 0, '.', ',');
      if (name === 'amount_with_apostrophe_separator') return group(cents, 2, "'", '.');
      return group(cents, 2, ',', '.');
    });
  }

  var painting = false;
  function setText(node, text) {
    if (node && node.textContent !== text) node.textContent = text;
  }

  // Writes the backend's prices into every cart view.
  function paint(cart, data) {
    if (!data || data.mode !== 'DRAFT_ORDER') return;
    painting = true;
    try {
      var byKey = {};
      (data.lines || []).forEach(function (l) { byKey[l.key] = l; });
      var problems = {};
      (data.errors || []).forEach(function (e) { if (e.key) problems[e.key] = e.message; });

      // A prescription line the backend could not price (no longer
      // available) keeps the cart's own figures and shows why.
      var total = 0;
      var allPriced = true;
      (cart.items || []).forEach(function (item) {
        var l = byKey[item.key];
        if (l && l.lineTotal != null) total += Number(l.lineTotal);
        else if (isDraftLine(item)) allPriced = false;
        else total += item.final_line_price / 100;
      });

      var lines = document.querySelectorAll('[data-pdc-line]');
      for (var i = 0; i < lines.length; i += 1) {
        var el = lines[i];
        var lineKey = el.getAttribute('data-pdc-line');
        var stockNote = el.querySelector('[data-pdc-stock-note]');
        if (stockNote && (problems[lineKey] || stockNote.hasAttribute('data-basari-note'))) {
          setText(stockNote, problems[lineKey] || '');
          stockNote.hidden = !problems[lineKey];
          stockNote.setAttribute('data-basari-note', '');
        }
        var l = byKey[lineKey];
        if (!l || l.kind !== 'BASARI' || l.unitPrice == null) continue;
        el.setAttribute('data-unit-final', String(Math.round(l.unitPrice * 100)));
        el.setAttribute('data-unit-original', String(Math.round(l.unitPrice * 100)));
        setText(el.querySelector('[data-pdc-line-price]'), formatMoney(l.lineTotal));
        var compare = el.querySelector('[data-pdc-line-compare]');
        if (compare) compare.hidden = true;
        var unitLine = el.querySelector('[data-pdc-unit-line]');
        if (unitLine && unitLine.lastChild && unitLine.lastChild.nodeType === 3) {
          var text = ' · ' + formatMoney(l.unitPrice) + ' each';
          if (unitLine.lastChild.nodeValue.trim() !== text.trim()) unitLine.lastChild.nodeValue = text;
        }
      }
      var subs = allPriced ? document.querySelectorAll('[data-pdc-subtotal]') : [];
      for (var s = 0; s < subs.length; s += 1) {
        subs[s].setAttribute('data-cents', String(Math.round(total * 100)));
        setText(subs[s], formatMoney(total));
      }
    } finally {
      painting = false;
    }
  }

  var reviewTimer = null;
  var reviewSeq = 0;
  function refreshReview(now) {
    clearTimeout(reviewTimer);
    reviewTimer = setTimeout(function () {
      var seq = (reviewSeq += 1);
      readCart()
        .then(function (cart) {
          if (!hasDraftLines(cart)) return;
          var sig = signature(cart);
          // Unchanged cart: repaint from the last answer, no request.
          if (lastPreview && lastPreview.signature === sig) return paint(cart, lastPreview.data);
          return api('/api/storefront/checkout/preview', { lines: draftLines(cart) }).then(function (r) {
            if (seq !== reviewSeq || !r.body || !r.body.data) return;
            lastPreview = { signature: sig, data: r.body.data };
            paint(cart, r.body.data);
          });
        })
        .catch(function () {
          /* the payment step checks again */
        });
    }, now ? 0 : 300);
  }

  // The cart views are re-rendered by premium-cart.js after every change. Only
  // changes inside a cart view count; the rest of the page is ignored.
  function touchesCart(m) {
    var t = m.target && m.target.nodeType === 1 ? m.target : m.target && m.target.parentElement;
    if (t && t.closest && t.closest('[data-pdc-view]')) return true;
    return Array.prototype.some.call(m.addedNodes || [], function (n) {
      return n.nodeType === 1 && (n.matches('[data-pdc-view]') || !!n.querySelector('[data-pdc-line]'));
    });
  }
  function watchCart() {
    if (!API_BASE_URL || typeof MutationObserver !== 'function') return;
    new MutationObserver(function (mutations) {
      if (!painting && mutations.some(touchesCart)) refreshReview(false);
    }).observe(document.body, { childList: true, subtree: true, characterData: false });
    refreshReview(true);
  }

  // ---- entry points --------------------------------------------------------
  // One path to payment for every button and script: Draft Order when the cart
  // holds such a line, otherwise the older check and Shopify's /checkout.
  function goToCheckout(target) {
    return readCart().then(function (cart) {
      if (!API_BASE_URL) {
        if (hasDraftLines(cart)) {
          showCheckoutMessage(PAYMENT_OFF);
          return 'stopped';
        }
        return passThrough(target);
      }
      if (hasDraftLines(cart)) {
        return startDraftCheckout(cart).then(function (outcome) {
          if (outcome === 'fallback') return variantCheckout(cart, target);
          return outcome;
        });
      }
      return variantCheckout(cart, target);
    }, function () {
      showCheckoutMessage(NETWORK);
      return 'stopped';
    });
  }

  // Shopify's own checkout, unchecked.
  function passThrough(target) {
    if (target && !target.hasAttribute('data-basari-draft-pay')) {
      passed = true;
      target.click();
    } else {
      window.location.href = '/checkout';
    }
    return 'redirected';
  }

  function variantCheckout(cart, target) {
    return validate(cart).then(function (result) {
      if (!result.valid) {
        window.alert(refusalText(result));
        return 'stopped';
      }
      // A Draft Order cart's button is not a Shopify checkout button.
      if (target && !target.hasAttribute('data-basari-draft-pay')) {
        passed = true;
        target.click();
      } else {
        window.location.href = '/checkout';
      }
      return 'redirected';
    });
  }

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target instanceof Element ? event.target.closest(CHECKOUT_SELECTOR) : null;
      if (!target) return;
      if (passed) {
        passed = false;
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      if (target.getAttribute('aria-busy') === 'true') return;
      goToCheckout(target);
    },
    true
  );

  window.BasariCheckout = {
    // Buy Now on a Draft Order product page.
    start: function () {
      return goToCheckout(null);
    },
    // For theme scripts that would otherwise send the shopper to /checkout.
    goToCheckout: function () {
      return goToCheckout(null);
    },
    refresh: function () {
      lastPreview = null;
      refreshReview(true);
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchCart);
  else watchCart();
})();
